import React, { useContext, useEffect, useMemo } from 'react';
import { PromiseState } from 'react-refetch';
import first from 'lodash.first';
import delve from 'dlv';

import getStreamingKPIURL from 'url-constructors/getStreamingKPIURL';
import getTrendUrl from 'url-constructors/getTrendUrl';
import getBreakdownURL from 'url-constructors/getBreakdownURL';

import salesTypes from 'maps/sales-types';

import { EntityContext } from 'context/entityContext';
import connect from 'helpers/apiConnect';
import useCombinedRefetchState from 'hooks/useCombinedRefetchState';
import useUrlForTopic from 'components/data-containers/Overview/shared/utils/useUrlForTopic';

import OverviewContainer from 'components/Overview/Container/Container';
import {
  Grid,
  SourceBreakdownTitle,
} from 'components/Overview/Container/shared.style';

import TrendChart from '../shared/TrendChart';
import StreamingKPIs from '../shared/KPIsData';
import BreakdownTable from './BreakdownTable';
import metrics from 'maps/metrics';
import getSourcesBreakdownUrl from 'url-constructors/getSourcesBreakdownUrl';
import SourcesBreakdownTable from './SourcesBreakdownTable';
import CustomerIcon from 'components/base/CustomerIcon/CustomerIcon';
import { getLocalSortBy } from '../OverallPerformance/OverallPerformance';

function dataSources(): any {
  return {
    fetchKPIData(args: any): any {
      return { kpiData: getStreamingKPIURL(args) };
    },
    fetchTrendData(args: any): any {
      return { trendData: getTrendUrl(args) };
    },
    fetchBreakdownData(args: any): any {
      return { breakdownData: getBreakdownURL(args) };
    },
    fetchSpotifyBreakdownData(args: any): any {
      return {
        spotifyBreakdownData: getSourcesBreakdownUrl({
          ...args,
          customerFilter: '1',
        }),
      };
    },
    fetchAppleBreakdownData(args: any): any {
      return {
        appleBreakdownData: getSourcesBreakdownUrl({
          ...args,
          customerFilter: '2',
        }),
      };
    },
  };
}

interface IStreamingPerformanceProps {
  fetchBreakdownData: Function;
  fetchKPIData: Function;
  fetchTrendData: Function;
  fetchSpotifyBreakdownData: Function;
  spotifyBreakdownData: PromiseState<any>;
  fetchAppleBreakdownData: Function;
  appleBreakdownData: PromiseState<any>;
  breakdownData: PromiseState<any>;
  kpiData: PromiseState<any>;
  trendData: PromiseState<any>;
}

/*
 * The streaming performance card shows a trend line from the last 30 days and
 * a breakdown table of the top DSPs.
 *
 * Note: as the `sortBy` parameter differs per endpoint, the global sort metric
 * is converted to one supported by this data container.
 */
const StreamingPerformance: React.FC<IStreamingPerformanceProps> = (props) => {
  const {
    fetchKPIData,
    kpiData = PromiseState.create(),
    fetchTrendData,
    trendData = PromiseState.create(),
    fetchBreakdownData,
    breakdownData = PromiseState.create(),
    fetchSpotifyBreakdownData,
    spotifyBreakdownData = PromiseState.create(),
    fetchAppleBreakdownData,
    appleBreakdownData = PromiseState.create(),
  } = props;

  const ctx = useContext(EntityContext);
  const {
    compareMode,
    countryIds,
    filters,
    entityIds,
    entityType,
    viewSettings,
    customRegionFilter,
  } = ctx;
  const timeframe = viewSettings?.timeframe ?? 'twtd';

  useEffect(() => {
    const args = {
      entityIds,
      entityType,
      salesType: salesTypes.streaming.value,
      countryIds:
        compareMode === 'country' ? countryIds : filters?.country ?? undefined,
      region: filters?.region,
      customRegion: customRegionFilter,
      compareMode,
    };
    fetchKPIData({ ...args });
    fetchBreakdownData({ ...args });
    fetchSpotifyBreakdownData(args);
    fetchAppleBreakdownData(args);
    fetchTrendData({ ...args, period: 'day' });
  }, [
    compareMode,
    countryIds,
    entityIds,
    entityType,
    fetchBreakdownData,
    fetchKPIData,
    fetchTrendData,
    fetchSpotifyBreakdownData,
    fetchAppleBreakdownData,
    filters,
    customRegionFilter,
  ]);

  const [, isPending] = useCombinedRefetchState([
    breakdownData,
    kpiData,
    trendData,
    spotifyBreakdownData,
    appleBreakdownData,
  ]);

  const sortByValue = getLocalSortBy(timeframe);
  const sortByMetric = metrics[sortByValue];
  const rtdMetric = metrics['count_rtd'];

  const kpis: any = useMemo(() => {
    if (kpiData.fulfilled) {
      const data = kpiData.value;
      return first(data);
    }
  }, [kpiData]);

  const value = delve(kpis, ['data', sortByMetric.value], 0);
  const change = sortByMetric.change
    ? delve(kpis, ['data', sortByMetric.change], 0)
    : 0;
  const rtdValue = delve(kpis, ['data', rtdMetric.value], 0);
  return (
    <OverviewContainer
      filtersToShow={['country', 'region', 'customRegion']}
      title={'Streaming Performance'}
      subtitle={salesTypes.streaming.label}
      linkTo={useUrlForTopic('streaming')}
    >
      <Grid data-pw="streaming-grid-container">
        <div>
          <StreamingKPIs
            rtdValue={rtdValue}
            value={value}
            change={change}
            isPending={isPending}
            title={sortByMetric.label}
          />
          <TrendChart isPending={isPending} trendData={trendData} />
        </div>
        <BreakdownTable
          isPending={isPending}
          breakdownData={breakdownData.value}
          activeMetric={sortByMetric}
        />
      </Grid>
      <Grid data-pw="streaming-top-grid-container">
        <div>
          <SourceBreakdownTitle>
            <CustomerIcon aria-hidden customer="Spotify" />
            Top Spotify sources
          </SourceBreakdownTitle>
          <SourcesBreakdownTable
            isPending={isPending}
            breakdownData={spotifyBreakdownData.value}
            activeMetric={sortByMetric}
          />
        </div>
        <div>
          <SourceBreakdownTitle>
            <CustomerIcon aria-hidden customer="AppleMusic" />
            Top Apple sources
          </SourceBreakdownTitle>
          <SourcesBreakdownTable
            isPending={isPending}
            breakdownData={appleBreakdownData.value}
            activeMetric={sortByMetric}
          />
        </div>
      </Grid>
    </OverviewContainer>
  );
};

export default connect(dataSources as any)(StreamingPerformance as any);
